import { useState, useRef, useEffect } from "react";
import { XIcon } from "../icons/Icons";

async function sendMessage(message, addMessage, setStreaming) {
  const response = await fetch("https://career-agent-backend-c2ha.onrender.com/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message }),
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let fullText = "";
  setStreaming(true);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    fullText += chunk;
    addMessage({ role: "assistant", content: fullText }, true);
  }

  setStreaming(false);
}

export default function AgentModal({ open, onClose }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!open) setMessages([]);
  }, [open]);

  if (!open) return null;

  const addMessage = (msg, replaceLast = false) => {
    setMessages((prev) => {
      if (replaceLast && prev[prev.length - 1]?.role === "assistant") {
        return [...prev.slice(0, -1), msg];
      }
      return [...prev, msg];
    });
  };

  const handleSend = () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input };
    addMessage(userMsg);
    setInput("");
    sendMessage(userMsg.content, addMessage, setStreaming);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="ml-auto w-full max-w-md h-full bg-white dark:bg-slate-950 shadow-2xl flex flex-col animate-slideInRight"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="profile.jpg"
              alt="David Iguta"
              className="w-8 h-8 rounded-full object-cover"
            />
            <h3 className="font-semibold text-gray-800 dark:text-gray-200">
              David's AI Agent
            </h3>
          </div>
          <button
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800"
            onClick={onClose}
            aria-label="Close"
          >
            <XIcon className="w-5 h-5 text-gray-500 dark:text-gray-300" />
          </button>
        </div>

        {/* Avatar intro */}
        {messages.length === 0 && (
          <div className="flex flex-col items-center p-6 text-center">
            <img
              src="profile.jpg"
              alt="David Iguta"
              className="w-20 h-20 rounded-full object-cover mb-3 border border-gray-200 dark:border-white/10"
            />
            <h4 className="font-semibold text-gray-800 dark:text-gray-200">
              David Ndung'u
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              I can answer questions about David’s projects, skills, and
              academic journey.
            </p>
          </div>
        )}

        {/* Chat feed */}
        <div className="flex-1 p-4 overflow-y-auto space-y-4 text-sm">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${
                msg.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`px-4 py-2 rounded-2xl max-w-[75%] shadow ${
                  msg.role === "user"
                    ? "bg-indigo-500 text-white rounded-br-none"
                    : "bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-200 rounded-bl-none"
                }`}
              >
                {msg.content}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 dark:border-white/10 flex items-center gap-2 bg-gray-50 dark:bg-slate-950">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your question..."
            className="flex-1 bg-gray-100 dark:bg-slate-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-white/10 rounded-full px-4 py-2 outline-none focus:ring-2 focus:ring-indigo-400"
            disabled={streaming}
          />
          <button
            onClick={handleSend}
            disabled={streaming}
            className="px-4 py-2 rounded-full bg-indigo-500 text-white font-semibold hover:bg-indigo-400 disabled:opacity-50"
          >
            ➤
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
